import React, { type ReactNode, useState } from 'react';
import { Menu, ChevronsLeft, ChevronsRight, Home, ShoppingBag, Hammer, Sprout, Pickaxe, Settings, Cpu, BookOpen } from 'lucide-react';

interface LayoutProps {
    children: ReactNode;
    headerContent?: ReactNode;
    currentView?: string;
    onNavigate?: (view: string) => void;
}

// default currentView set to 'market'
export const Layout = ({ children, headerContent, currentView = 'market', onNavigate }: LayoutProps) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    return (
        <div style={{
            display: 'flex',
            height: '100vh',
            background: 'var(--color-bg-app)',
            color: 'var(--color-text-primary)'
        }}>
            {/* Sidebar */}
            <aside style={{
                width: isSidebarOpen ? '240px' : '64px',
                background: 'var(--color-bg-panel)',
                borderRight: '1px solid var(--color-border)',
                transition: 'width 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 10,
            }}>
                <div style={{
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isSidebarOpen ? 'space-between' : 'center',
                    height: '64px',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    {isSidebarOpen && <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-primary)' }}>Idlescape</h1>}
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--color-text-secondary)',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {isSidebarOpen ? <ChevronsLeft size={20} /> : <ChevronsRight size={20} />}
                    </button>
                </div>

                <nav style={{ padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>

                    <NavItem
                        icon={<ShoppingBag size={20} />}
                        label="Market"
                        isOpen={isSidebarOpen}
                        active={currentView === 'market'}
                        onClick={() => onNavigate?.('market')}
                    />
                    <NavItem
                        icon={<BookOpen size={20} />}
                        label="Compendium"
                        isOpen={isSidebarOpen}
                        active={currentView === 'compendium'}
                        onClick={() => onNavigate?.('compendium')}
                    />
                    <NavItem
                        icon={<Cpu size={20} />}
                        label="Tech Tree"
                        isOpen={isSidebarOpen}
                        active={currentView === 'tech_tree'}
                        onClick={() => onNavigate?.('tech_tree')}
                    />

                    {/* Skills Group */}
                    {isSidebarOpen && <div style={{ padding: '8px 12px', fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '8px' }}>Skills</div>}
                    <NavItem
                        icon={<Pickaxe size={20} />}
                        label="Mining"
                        isOpen={isSidebarOpen}
                        active={currentView === 'mining'}
                        onClick={() => onNavigate?.('mining')}
                    />
                    <NavItem
                        icon={<Sprout size={20} />}
                        label="Farming"
                        isOpen={isSidebarOpen}
                        active={currentView === 'farming'}
                        onClick={() => onNavigate?.('farming')}
                    />
                    <NavItem
                        icon={<Hammer size={20} />}
                        label="Crafting"
                        isOpen={isSidebarOpen}
                        active={currentView === 'crafting'}
                        onClick={() => onNavigate?.('crafting')}
                    />

                    <div style={{ flex: 1 }} /> {/* Spacer to push settings down? or just separator */}
                    <NavItem
                        icon={<Settings size={20} />}
                        label="Settings"
                        isOpen={isSidebarOpen}
                        active={currentView === 'settings'}
                        onClick={() => onNavigate?.('settings')}
                    />
                </nav>
            </aside>

            {/* Main Content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Header */}
                <header style={{
                    height: '64px',
                    padding: '0 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid var(--color-border)',
                    background: 'rgba(15, 23, 42, 0.8)',
                    backdropFilter: 'blur(8px)',
                }}>
                    {headerContent}
                </header>

                {/* Scrollable Content Area */}
                <main style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px',
                    scrollBehavior: 'smooth'
                }}>
                    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
};

const NavItem = ({ icon, label, isOpen, active = false, onClick }: { icon: ReactNode, label: string, isOpen: boolean, active?: boolean, onClick?: () => void }) => {
    return (
        <div
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px',
                borderRadius: '8px',
                cursor: 'pointer',
                background: active ? 'var(--color-bg-panel-hover)' : 'transparent',
                color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                gap: '12px',
                justifyContent: isOpen ? 'flex-start' : 'center',
                transition: 'all 0.2s'
            }}
        >
            {icon}
            {isOpen && <span style={{ fontWeight: 500 }}>{label}</span>}
        </div>
    )
}
